// Character limit for search field in autocomplete component for lists
export const CHAR_LIMIT_FOR_SEARCH: number = 3;

// Error message to be consoled in case of adding cpns
export const CPN_EDIT_ERROR_MESSGAE: string = 'Error in adding cpns';

// CPN max edit limit for add and modify associations
export const CPN_MAX_EDIT_LIMIT: number = 200;

// List name typing debounce time
export const CREATE_LIST_NAME_TYPING_DEBOUNCE: number = 500;

// Error status code to be returned when user list info is not available in 'edit' & 'duplicate' modes
export const CUSTOM_ERROR_STATUS_CODE_LIST_INFO_NOT_AVAILABLE: number = 456;

// Error status code to be returned when user list limit reached
export const CUSTOM_ERROR_STATUS_CODE_LIST_LIMIT_REACHED: number = 455;

// Default value for max number of suggestions in autocomplete component
export const DEFAULT_AUTOCOMPLETE_LIMIT: number = 30;

// Default value for max number of items that can be edited
export const DEFAULT_ITEM_EDIT_LIMIT: number = 200;

// Default value for list limit in case api call fails or returns invalid response
export const DEFAULT_LIST_LIMIT: number = 5;

// Default value for list items limit in case api call fails or returns invalid response
export const DEFAULT_LIST_ITEMS_LIMIT: number = 20;

// Default value for tag limit in case api call fails or returns invalid response
export const DEFAULT_TAG_LIMIT: number = 10;

// Api calls default timeout value
export const LIST_API_TIMEOUT_VALUE: number = 12000;

// Maximum characters allowed in List name
export const LIST_NAME_CHAR_LIMIT: number = 30;

// Private list type ti-radio value
export const LIST_TYPE_PRIVATE: string = 'private';

// Shared list type ti-radio value
export const LIST_TYPE_SHARED: string = 'shared';

// Super admin role text
export const SUPER_ADMIN_ROLE: string = 'SUPER_ADMIN';

// Regex for valid list name pattern
export const VALID_LIST_NAME_PATTERN = '^(?!\\s*$)[\\w\\s-]+$';

/**
 * Add CPNs Status
 */
export const enum ADD_CPNS_STATUS {
    CPN_LENGTH_VIOLATION = 'CPN_LENGTH_VIOLATION',
    NO_PART_FOUND_VIOLATION = 'NO_PART_FOUND_VIOLATION',
    EXISTING_CPN_VIOLATION = 'EXISTING_CPN_VIOLATION',
    CPN_ASSOC_LIMIT_VIOLATION = 'CPN_ASSOC_LIMIT_VIOLATION',
    OK = 'OK'
}

// Supported modes for add to list
export type AddToListMode = 'create-list' | 'list-search' | 'opn-search-for-cpn' | 'opn-search-for-list' | 'upload';

// Supported error modes for add to list component
export type AddToListErrorContext = 'list-limit' | 'create-list-error' | 'init-error';

// Supported operation types for add to list component
export type AddToListOperations = 'modify-list' | 'modify-tags' | 'modify-items' | 'add-items' | 'remove-items';

// Create list mode context
export type CreateListMode = 'create' | 'edit' | 'duplicate';

// Supported Http Method types
export type HttpMethodType = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// TO DO: might want to import interfaces from @ticom/list-manager-components
export interface AppSettings {
    cpnAssocFetchLimit: number;
    cpnAssocPerPartLimit: number;
    cpnListMaxItemsEditLimit: number;
    enabledInvCGX: number;
    maxInvCallsPerTIer: number;
    maxInvCallsPerDirect: number;
    maxInvCallsPerMassMarket: number;
    maxInvCallsPerCGX: number;
    maxInvPartsPerCall: number;
    maxItemPerListTIer: number;
    maxItemPerListDirect: number;
    maxItemPerListMassMarket: number;
    maxItemPerListCGX: number;
    maxListPerTIer: number;
    maxListPerDirect: number;
    maxListPerMassMarket: number;
    maxListPerCGX: number;
    maxTagsPerList: number;
    enabledUploadCGX: number;
    enabledDcofCGX: number;
    maxDcofCallsPerTIer: number;
    maxDcofCallsPerDirect: number;
    maxDcofCallsPerMassMarket: number;
    maxDcofCallsPerCGX: number;
    maxDcofPartsPerCall: number;
    autocompleteLimit: number;
}

export interface CpnAssoc {
    cpId: number;
    customerPartNumber: string;
    associatedPartNumber: string;
    associatedPartType?: string;
    associatedOpenOrder?: boolean;
    editLocked?: boolean;
    modifiedDate?: Date;
    statusCode?: string;
    statusDescription?: string;
    existsInOtherLevel?: boolean;
}

export interface ListInfo {
    listId: number;
    name: string;
    type: string;
    quantityMultiplier: number;
    createdDate: string;
    modifiedDate: string;
    modifiedBy: string;
    ownerPermId: string;
    ownerName: string;
    sharedCpId: number;
    items: ListItem[];
    totalItemCount: number;
    maxItemLimit: number;
    nextPageItemIndex: number;
    tags: TagInfo[];
    errors?: string[];
    salesConsent: boolean;
}

export interface ListItem {
    itemId: number;
    listId: number;
    partId: string;
    partNumber: string;
    type: string;
    customerPartNumber: string;
    quantity: number;
    itemInfo: ListItemInfo;
}

export interface ListItemInfo {
    getListItemInfo?: () => void;
}

export interface TagInfo {
    tagId: number;
    listId: number;
    name: string;
}

export interface UserInfo {
    permId: number;
    email: string;
    currentCpId: number;
    parentCpId: number;
    childCompanies: Map<number, string>;
    isInternal: boolean;
    isCGX: boolean;
    role: string;
    userType: string;
    isBacklog: boolean;
    canBacklogOrder: boolean;
    canRequestQuote: boolean;
}

export interface MyTiListManagerBasicEventDetail {
    contextId: string;
}

export interface MyTiListManagerErrorEventDetail extends MyTiListManagerBasicEventDetail {
    errorCode: number;
    errorMsg: string;
}

export interface MyTiCreateListEditSuccessEventDetail extends MyTiListManagerBasicEventDetail {
    editedList: ListInfo;
}

export interface MyTiCreateListSuccessEventDetail extends MyTiListManagerBasicEventDetail {
    createdList: ListInfo;
}

export interface MyTiCreateListValidNameChangeEventDetail extends MyTiListManagerBasicEventDetail {
    isListNameValid: boolean;
}

export interface MyTiCreateListEditValidChangeEventDetail extends MyTiListManagerBasicEventDetail {
    isEditValid: boolean;
}

export interface MyTiSearchListSelectedEventDetail extends MyTiListManagerBasicEventDetail {
    selectedList: ListInfo;
    modifiedTags: boolean;
}

export interface MyTiBulkAddtoListEventDetail extends MyTiListManagerBasicEventDetail {
    isValid: boolean;
    action: AddToListMode;
}

export interface MyTiAddToCpnsEventDetail extends MyTiListManagerBasicEventDetail {
    error?: boolean;
    addedCpnAssocs?: CpnAssoc[];
    errorCpnAssocs?: CpnAssoc[];
}

export interface MyTiAddToListEventDetail extends MyTiListManagerBasicEventDetail {
    error?: boolean;
    listOnSuccess?: ListInfo;
    newItems?: ListItem[];
    oldItems?: ListItem[];
    operations?: AddToListOperations[];
}
